import React, { useState, useEffect, useCallback } from 'react';
import { COLORS, SPACING } from '../styles/theme';
import { Post, getFeedPosts, getThread, PostFeedMode, getPinnedPostIds } from '../lib/posts';
import { PostCard } from './PostCard';
import { PostComposer } from './PostComposer';
import { loadOrCreateIdentity } from '../lib/identity';

const MODES: Array<{ key: PostFeedMode; label: string }> = [
  { key: 'foryou',    label: '★ for you' },
  { key: 'trending',  label: '▲ trending' },
  { key: 'following', label: '♥ following' },
  { key: 'local',     label: '◈ local' },
  { key: 'fresh',     label: '↓ fresh' },
];

export function PostFeed() {
  const [mode, setMode] = useState<PostFeedMode>('fresh');
  const [posts, setPosts] = useState<Post[]>([]);
  const [threadPostId, setThreadPostId] = useState<string | null>(null);
  const [threadPosts, setThreadPosts] = useState<Post[]>([]);

  const identity = loadOrCreateIdentity();

  const [pinnedIds, setPinnedIds] = useState<Set<string>>(() => new Set(getPinnedPostIds()));

  const reload = useCallback(() => {
    const pinned = new Set(getPinnedPostIds());
    setPinnedIds(pinned);
    const fetched = getFeedPosts(mode, identity.userId);
    const pinnedPosts = fetched.filter(p => pinned.has(p.id));
    const rest = fetched.filter(p => !pinned.has(p.id));
    setPosts([...pinnedPosts, ...rest]);
  }, [mode, identity.userId]);

  useEffect(() => { reload(); }, [reload]);

  const openThread = (postId: string) => {
    setThreadPostId(postId);
    setThreadPosts(getThread(postId));
  };

  const refreshThread = useCallback((postId: string) => {
    setThreadPosts(getThread(postId));
  }, []);

  if (threadPostId) {
    const rootPost = threadPosts[0];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ padding: SPACING.md, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: SPACING.md, flexShrink: 0 }}>
          <button
            onClick={() => { setThreadPostId(null); reload(); }}
            style={{ background: 'none', border: 'none', color: COLORS.highlight, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', fontSize: '13px', padding: 0 }}
          >
            ← posts
          </button>
          <span style={{ fontSize: '11px', color: COLORS.textMuted, fontFamily: '"IBM Plex Mono", monospace' }}>thread</span>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {rootPost && (
            <PostCard
              post={rootPost}
              allPosts={threadPosts}
              depth={0}
              onReloadFeed={() => refreshThread(threadPostId)}
            />
          )}
        </div>
        {rootPost && (
          <PostComposer
            replyTo={{ pubkey: rootPost.pubkey, postId: threadPostId }}
            onPosted={() => refreshThread(threadPostId)}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', gap: SPACING.xs, padding: SPACING.md, borderBottom: `1px solid ${COLORS.border}`, overflowX: 'auto', flexShrink: 0 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              background: mode === m.key ? COLORS.accent : 'none',
              border: `1px solid ${mode === m.key ? COLORS.accent : COLORS.border}`,
              color: mode === m.key ? '#fff' : COLORS.text,
              padding: `${SPACING.xs} ${SPACING.md}`,
              cursor: 'pointer',
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '11px',
              whiteSpace: 'nowrap',
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      <PostComposer onPosted={reload} />

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {posts.length === 0 && (
          <div style={{ padding: SPACING.xl, color: COLORS.textMuted, fontFamily: '"IBM Plex Mono", monospace', textAlign: 'center', fontSize: '13px' }}>
            no posts yet — be the first
          </div>
        )}
        {posts.map(post => (
          <PostCard
            key={post.id}
            post={post}
            allPosts={[]}
            depth={0}
            isPinned={pinnedIds.has(post.id)}
            onOpenThread={openThread}
            onReloadFeed={reload}
          />
        ))}
      </div>
    </div>
  );
}
